import React, { useCallback, useEffect, useId, useState } from 'react';
import { Star, Trash2 } from 'lucide-react';
import { ConfirmDialog, useToast } from '../../common';
import Icd10Picker from '../../common/Icd10Picker';
import { DiagnosesService } from '../../../services/patientmr/diagnoses.service';
import type { Icd10Code } from '../../../dtos/reference/icd10.dto';
import type { Diagnosis } from '../../../dtos/patientmr/diagnosis.dto';

export interface DiagnosisListProps {
  patientId: string;
  readOnly: boolean;
}

/**
 * Patient diagnosis list (PAT-05): ICD-10 search over the full CMS code set (Icd10Picker; only
 * billable codes can be added), exactly one primary diagnosis, resolve and delete (soft delete).
 */
const DiagnosisList: React.FC<DiagnosisListProps> = ({ patientId, readOnly }) => {
  const baseId = useId();
  const { showToast } = useToast();
  const [diagnoses, setDiagnoses] = useState<Diagnosis[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState<Diagnosis | null>(null);

  const [selected, setSelected] = useState<Icd10Code | null>(null);
  const [onsetDate, setOnsetDate] = useState('');

  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setLoadError(null);
    try {
      setDiagnoses(await DiagnosesService.list(patientId));
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Unable to load diagnoses.');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (action: () => Promise<unknown>, success: string) => {
    setBusy(true);
    try {
      await action();
      showToast(success, { variant: 'success' });
      await load();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'The diagnosis change was not saved.', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const activeCount = diagnoses.filter((d) => d.status === 'ACTIVE').length;

  const addDiagnosis = () => {
    if (!selected) return;
    const isFirst = !diagnoses.some((d) => d.isPrimary);
    void run(async () => {
      await DiagnosesService.create(patientId, {
        icd10Code: selected.code,
        description: selected.description,
        isPrimary: isFirst,
        onsetDate: onsetDate || null,
        status: 'ACTIVE',
      });
      setSelected(null);
      setOnsetDate('');
    }, 'Diagnosis added.');
  };

  const id = (name: string) => `${baseId}-${name}`;

  return (
    <div className="text-left" data-testid="diagnosis-list">
      {loading && <p className="text-sm text-slate-500">Loading diagnoses…</p>}
      {loadError && (
        <p role="alert" className="text-sm text-red-700">
          {loadError}{' '}
          <button type="button" className="underline" onClick={() => void load()}>
            Try again
          </button>
        </p>
      )}
      {!loading && !loadError && diagnoses.length === 0 && <p className="text-sm text-slate-500">No diagnoses recorded.</p>}

      {diagnoses.length > 0 && (
        <ul className="divide-y divide-slate-200 rounded-md border border-slate-200" aria-label="Diagnoses">
          {diagnoses.map((d) => (
            <li key={d.diagnosisId} className="flex flex-wrap items-center justify-between gap-2 p-2.5">
              <div className="min-w-0 text-sm">
                <span className="font-mono font-medium text-slate-800">{d.icd10Code}</span>{' '}
                <span className={d.status === 'RESOLVED' ? 'text-slate-400 line-through' : 'text-slate-700'}>{d.description}</span>
                {d.isPrimary && (
                  <span className="ml-2 rounded border border-indigo-200 bg-indigo-50 px-1.5 py-0.5 text-[11px] font-semibold text-indigo-800">Primary</span>
                )}
                {d.status === 'RESOLVED' && (
                  <span className="ml-2 rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[11px] text-slate-600">Resolved</span>
                )}
              </div>
              {!readOnly && (
                <div className="flex flex-wrap gap-1">
                  {!d.isPrimary && d.status === 'ACTIVE' && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void run(() => DiagnosesService.update(patientId, d.diagnosisId, { isPrimary: true }), 'Primary diagnosis updated.')}
                      className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    >
                      <Star size={12} aria-hidden="true" /> Make primary
                    </button>
                  )}
                  {d.status === 'ACTIVE' ? (
                    <button
                      type="button"
                      disabled={busy || (d.isPrimary && activeCount > 1)}
                      title={d.isPrimary && activeCount > 1 ? 'Make another diagnosis primary first' : undefined}
                      onClick={() => void run(() => DiagnosesService.update(patientId, d.diagnosisId, { status: 'RESOLVED', isPrimary: false }), 'Diagnosis resolved.')}
                      className="rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    >
                      Resolve
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void run(() => DiagnosesService.update(patientId, d.diagnosisId, { status: 'ACTIVE' }), 'Diagnosis reactivated.')}
                      className="rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    >
                      Reactivate
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setDeleting(d)}
                    className="rounded p-1.5 text-red-600 hover:bg-red-50 disabled:opacity-50"
                    aria-label={`Delete diagnosis ${d.icd10Code}`}
                  >
                    <Trash2 size={14} aria-hidden="true" />
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {!readOnly && (
        <div className="mt-3 rounded-md border border-slate-200 p-3">
          <label htmlFor={id('search')} className="block text-sm font-medium text-slate-700">
            Add diagnosis (ICD-10)
          </label>
          <div className="mt-1">
            <Icd10Picker
              id={id('search')}
              value={selected}
              onChange={setSelected}
              placeholder="Search by code or description"
              disabled={busy}
              excludeCodes={diagnoses.filter((d) => d.status === 'ACTIVE').map((d) => d.icd10Code)}
            />
          </div>
          {selected && (
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2 items-end">
              <p className="text-sm text-slate-700">
                <span className="font-mono font-medium">{selected.code}</span> — {selected.description}
                {!diagnoses.some((d) => d.isPrimary) && <span className="ml-1 text-xs text-indigo-700">(will be primary)</span>}
              </p>
              <div>
                <label htmlFor={id('onset')} className="block text-xs font-medium text-slate-700">Onset date</label>
                <input id={id('onset')} type="date" value={onsetDate} onChange={(e) => setOnsetDate(e.target.value)} className="mt-1 rounded-md border border-slate-300 p-1.5 text-sm" />
              </div>
              <button
                type="button"
                disabled={busy}
                onClick={addDiagnosis}
                className="rounded-md bg-slate-800 px-3 py-2 text-sm text-white hover:bg-slate-700 disabled:opacity-50"
              >
                Add
              </button>
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        open={!!deleting}
        title="Delete diagnosis"
        message={deleting ? `Delete ${deleting.icd10Code} from the diagnosis list?` : ''}
        confirmLabel="Delete"
        destructive
        busy={busy}
        onConfirm={async () => {
          const target = deleting;
          setDeleting(null);
          if (target) await run(() => DiagnosesService.remove(patientId, target.diagnosisId), 'Diagnosis deleted.');
        }}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
};

export default DiagnosisList;
